import Vue from 'vue';
import axios from 'axios'; /* TODO: Setup and use Axios API instance */
import moment from 'moment';
import { endpoint, API_BASE_URL, BOOKING_API_ENDPOINTS } from '@/utils/api/endpoints';
import { Booking } from '@/models/booking.model';
import { createMap } from '@/utils/util';

export default {
	namespaced: true,

	state: {
		companyBookings: [],
		professionalBookings: [],
	},

	getters: {
		/* Company Bookings */
		companyBookings: state => state.companyBookings.map(x => new Booking(x)),
		companyBookingsMap: (_, getters) => createMap(getters.companyBookings),
		companyConfirmedBookings: (_, getters) => getters.companyBookings.filter(x => x.status === Booking.STATUS.CONFIRMED),
		companyCancelledBookings: (_, getters) => getters.companyBookings.filter(x => x.status === Booking.STATUS.CANCELLED),
		companyUpcomingBookings: (_, getters) =>
			getters.companyBookings.filter(
				x =>
					/* TODO: Confirm if date check works && and should time be taken into account aswell? */
					x.status === Booking.STATUS.CONFIRMED && moment(x.start_date).isSameOrAfter(moment().format(Booking.DATE_FORMAT), 'day')
			),
		companyBookingHistory: (_, getters) => [
			...getters.companyCancelledBookings,
			...getters.companyConfirmedBookings.filter(x => moment(x.start_date).isBefore(moment().format(Booking.DATE_FORMAT), 'day')),
		],

		/* Professional Bookings */
		professionalBookings: state => state.professionalBookings.map(x => new Booking(x)),
		professionalConfirmedBookings: (_, getters) => getters.professionalBookings.filter(x => x.status === Booking.STATUS.CONFIRMED),
		professionalUpcomingBookings: (_, getters) =>
			getters.professionalBookings.filter(x => x.status === Booking.STATUS.CONFIRMED && moment(x.start_date).isSameOrAfter(moment().format(Booking.DATE_FORMAT), 'day')),
		professionalCancelledBookings: (_, getters) => getters.professionalBookings.filter(x => x.status === Booking.STATUS.CANCELLED),
		professionalBookingHistory: (_, getters) => [
			...getters.professionalCancelledBookings,
			...getters.professionalConfirmedBookings.filter(x => moment(x.start_date).isBefore(moment().format(Booking.DATE_FORMAT), 'day')),
		],
		professionalBookingsRequests: (_, __, ___, rootGetters) => [
			...rootGetters['availability/professionalRequestsReceived'],
			...rootGetters['offers/professionalPrivateBookingRequests'],
		],
	},

	mutations: {
		SET_COMPANY_BOOKINGS: (state, data) => (state.companyBookings = data),
		UPDATE_COMPANY_BOOKINGS: (state, data) => {
			if (!Array.isArray(data)) data = [data];
			for (let value of data) {
				const index = state.companyBookings.findIndex(existing => existing.id === value.id);
				if (index == -1) {
					Vue.set(state.companyBookings, state.companyBookings.length, value);
				} else {
					Vue.set(state.companyBookings, index, value);
				}
			}
		},
		SET_PROFESSIONAL_BOOKINGS: (state, data) => (state.professionalBookings = data),
		UPDATE_PROFESSIONAL_BOOKINGS: (state, data) => {
			if (!Array.isArray(data)) data = [data];
			for (let value of data) {
				const index = state.professionalBookings.findIndex(existing => existing.id === value.id);
				if (index == -1) {
					Vue.set(state.professionalBookings, state.professionalBookings.length, value);
				} else {
					Vue.set(state.professionalBookings, index, value);
				}
			}
		},
	},

	actions: {
		async _LoadBookings({ rootGetters }, { endPoint, params, query }) {
			if (!endPoint) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			try {
				const url = endpoint(API_BASE_URL, endPoint, { params, query });
				const results = await axios.get(url, { headers: { Authorization: rootGetters.jwtToken } });
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load bookings' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async LoadCompanyBookings({ commit, dispatch, rootGetters }, { accountId, startDate, endDate, query = null }) {
			if (!startDate || !endDate) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (!accountId) accountId = rootGetters.company.id;

			try {
				const results = await dispatch('_LoadBookings', {
					endPoint: BOOKING_API_ENDPOINTS.COMPANY,
					query,
					params: {
						accountId,
						startDate,
						endDate,
					},
				});

				if (!results) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load bookings' });

				commit('UPDATE_COMPANY_BOOKINGS', results.data.Items);

				return results.data.Items;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async FetchCompanyBookings({ dispatch, rootGetters }, { accountId, startDate, endDate, query = null }) {
			if (!startDate || !endDate) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (!accountId) accountId = rootGetters.company.id;

			try {
				const results = await dispatch('_LoadBookings', {
					endPoint: BOOKING_API_ENDPOINTS.COMPANY,
					query,
					params: {
						accountId,
						startDate,
						endDate,
					},
				});

				if (!results) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load bookings' });

				const bookingsResults = results.data.Items;

				return bookingsResults.map(x => new Booking(x)).filter(x => x.status === Booking.STATUS.CONFIRMED);
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async LoadProfessionalBookings({ commit, dispatch, rootGetters }, { accountId, startDate, endDate, query = null }) {
			if (!startDate || !endDate) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (!accountId) accountId = rootGetters.user.id;
			if (typeof startDate !== 'string' || typeof endDate !== 'string') return;
			try {
				const results = await dispatch('_LoadBookings', {
					endPoint: BOOKING_API_ENDPOINTS.PROFESSIONAL,
					query,
					params: {
						accountId,
						startDate,
						endDate,
					},
				});
				if (!results) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load bookings' });

				commit('UPDATE_PROFESSIONAL_BOOKINGS', results.data.Items);
				return results.data.Items;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async FetchProfessionalBookings({ dispatch, rootGetters }, { accountId, startDate, endDate, query = null }) {
			if (!startDate || !endDate) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (!accountId) accountId = rootGetters.user.id;

			try {
				const results = await dispatch('_LoadBookings', {
					endPoint: BOOKING_API_ENDPOINTS.PROFESSIONAL,
					query,
					params: {
						accountId,
						startDate,
						endDate,
					},
				});

				if (!results) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load bookings' });

				const bookingsResults = results.data.Items;

				return bookingsResults.map(x => new Booking(x)).filter(x => x.status === Booking.STATUS.CONFIRMED);
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async CreateBooking(_, booking) {
			/* TODO: Complete booking create implementation */
			if (!booking) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (Array.isArray(booking) && typeof booking !== 'object') throw window.$Error(window.$ERROR.ERR_TYPE, { parent: 'Booking can only be an object' });

			try {
				const url = endpoint(API_BASE_URL, BOOKING_API_ENDPOINTS.CREATE);
				const results = await axios.post(url, { booking });
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to create or load booking' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async UpdateBooking(_, booking) {
			/* TODO: Complete booking create implementation */
			if (!booking) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (Array.isArray(booking) && typeof booking !== 'object') throw window.$Error(window.$ERROR.ERR_TYPE, { parent: 'Booking can only be an object' });

			try {
				const url = endpoint(API_BASE_URL, BOOKING_API_ENDPOINTS.UPDATE);
				const results = await axios.put(url, { booking });
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to update or load booking' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async UpsertBooking({ dispatch }, booking) {
			if (!booking) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });

			try {
				const result = await dispatch(booking.id ? 'UpdateBooking' : 'CreateBooking', booking);
				return result;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async DeleteBooking(_, booking) {
			/* TODO: Complete booking create implementation */
			if (!booking) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (Array.isArray(booking) && typeof booking !== 'object') throw window.$Error(window.$ERROR.ERR_TYPE, { parent: 'Booking can only be an object' });

			try {
				const url = endpoint(API_BASE_URL, BOOKING_API_ENDPOINTS.DELETE);
				const results = await axios.delete(url);
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to delete or load booking' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},
	},
};
